import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { detectLang } from '../lib/rtl';
import { STRINGS } from '../lib/i18n';
import { ReportModal } from './ReportModal';
import { maybeRenderChart } from './ChartBlock';

export type ChatBubble = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  status: 'streaming' | 'done' | 'error';
  mode?: 'A' | 'B' | 'refused' | null;
  /** Stage event signal for the streaming bubble (classify/retrieve/answer/cache/follow-up). */
  stage?: 'classify' | 'retrieve' | 'answer' | 'follow-up' | 'cache' | null;
  /** Kept on the type for backwards compat but no longer rendered in the UI. */
  model?: string;
  /** The user message that produced this assistant bubble — needed to attach to a report. */
  question?: string;
};

// Light-theme markdown renderers. The heavy lifting (table styling, alternating
// rows, line-height) lives in the `.tatweer-md` block in globals.css so that the
// bubble can be tightly themed without piling up Tailwind utility classes.
const markdownComponents = {
  h1: ({ children }: { children?: React.ReactNode }) => <h1>{children}</h1>,
  h2: ({ children }: { children?: React.ReactNode }) => <h2>{children}</h2>,
  h3: ({ children }: { children?: React.ReactNode }) => <h3>{children}</h3>,
  p: ({ children }: { children?: React.ReactNode }) => <p>{children}</p>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul>{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol>{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
  strong: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
  em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  code: ({ className, children }: { className?: string; children?: React.ReactNode }) => {
    // Render fenced ```chart ... ``` blocks as actual Recharts visualizations.
    // Anything else falls through to a plain inline/block <code>.
    if (className === 'language-chart' && typeof children === 'string') {
      const rendered = maybeRenderChart(children);
      if (rendered) return <>{rendered.node}</>;
    }
    return <code className={className}>{children}</code>;
  },
  pre: ({ children }: { children?: React.ReactNode }) => {
    // Detect chart blocks at the <pre> level too — react-markdown wraps fenced
    // blocks as <pre><code class="language-...">. We unwrap to render the chart
    // directly without a code-block frame around it.
    const child = (children as { props?: { className?: string; children?: React.ReactNode } } | undefined)?.props;
    if (child?.className === 'language-chart' && typeof child.children === 'string') {
      const rendered = maybeRenderChart(child.children);
      if (rendered) return <>{rendered.node}</>;
    }
    return <pre>{children}</pre>;
  },
  table: ({ children }: { children?: React.ReactNode }) => (
    <div className="my-2 overflow-x-auto"><table>{children}</table></div>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => <thead>{children}</thead>,
  tbody: ({ children }: { children?: React.ReactNode }) => <tbody>{children}</tbody>,
  tr: ({ children }: { children?: React.ReactNode }) => <tr>{children}</tr>,
  th: ({ children }: { children?: React.ReactNode }) => <th>{children}</th>,
  td: ({ children }: { children?: React.ReactNode }) => <td>{children}</td>,
  hr: () => <hr />,
  blockquote: ({ children }: { children?: React.ReactNode }) => <blockquote>{children}</blockquote>,
};

function StageIndicator({ stage, lang }: { stage: ChatBubble['stage']; lang: 'ar' | 'en' }) {
  const phrases = STRINGS.chat.stages;
  const key = (stage ?? 'answer') as keyof typeof phrases;
  const phrase = phrases[key]?.[lang] ?? phrases.answer[lang];
  return (
    <div className="flex items-center gap-2 text-tatweer-body-soft" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <span className="inline-flex items-center gap-1" aria-hidden="true">
        <span className="block w-1.5 h-1.5 rounded-full bg-tatweer-green animate-dot-pulse" style={{ animationDelay: '0ms' }} />
        <span className="block w-1.5 h-1.5 rounded-full bg-tatweer-green animate-dot-pulse" style={{ animationDelay: '160ms' }} />
        <span className="block w-1.5 h-1.5 rounded-full bg-tatweer-green animate-dot-pulse" style={{ animationDelay: '320ms' }} />
      </span>
      <span className="text-sm">{phrase}</span>
    </div>
  );
}

export function ChatStream({ bubbles, onRetry }: { bubbles: ChatBubble[]; onRetry?: (id: string) => void }) {
  const [reportingFor, setReportingFor] = useState<string | null>(null);
  const [submittedFor, setSubmittedFor] = useState<Record<string, boolean>>({});

  const reportingBubble = reportingFor ? bubbles.find((x) => x.id === reportingFor) : null;
  const reportingLang: 'ar' | 'en' = reportingBubble ? detectLang(reportingBubble.text || 'ا') : 'ar';

  function handleSuccess(id: string) {
    setSubmittedFor((m) => ({ ...m, [id]: true }));
    setReportingFor(null);
    // Clear the "submitted" inline confirmation after 3s
    setTimeout(() => setSubmittedFor((m) => { const { [id]: _, ...rest } = m; return rest; }), 3000);
  }

  return (
    <>
      <div className="flex flex-col gap-4 py-2">
        {bubbles.map((b) => {
          const lang = detectLang(b.text || 'ا');
          const isUser = b.role === 'user';
          const showStage = !isUser && b.status === 'streaming' && !b.text;
          const showFooter = !isUser && b.status === 'done' && b.mode && b.mode !== 'refused';
          const showReport = showFooter; // same condition: done, non-refused assistant bubble
          const reportLabel = lang === 'ar' ? 'ابلاغ عن مشكلة في الإجابة' : 'Report an issue';

          if (isUser) {
            return (
              <div key={b.id} className="flex justify-end">
                <div
                  className="max-w-[85%] rounded-2xl bg-tatweer-navy text-white px-5 py-3 shadow-bubble bidi-plaintext leading-relaxed whitespace-pre-wrap"
                  dir={lang === 'ar' ? 'rtl' : 'ltr'}
                >
                  {b.text}
                </div>
              </div>
            );
          }

          const isError = b.status === 'error';
          const retryLabel = lang === 'ar' ? 'إعادة المحاولة' : 'Retry';
          // Show report on done bubbles AND on refusals — refusals on legitimate
          // questions are exactly when users want to flag the bot.
          const showReportOnRefused = !isUser && b.status === 'done' && b.mode === 'refused';

          return (
            <div key={b.id} className="flex justify-start">
              <div
                className={`max-w-[88%] w-full rounded-2xl shadow-bubble px-5 py-4 bidi-plaintext ${isError
                  ? 'bg-rose-50 ring-1 ring-rose-300 text-rose-900'
                  : 'bg-white ring-1 ring-tatweer-line text-tatweer-body'}`}
                dir={lang === 'ar' ? 'rtl' : 'ltr'}
              >
                {showStage ? (
                  <StageIndicator stage={b.stage ?? 'classify'} lang={lang} />
                ) : (
                  <div className="tatweer-md">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                      {b.text}
                    </ReactMarkdown>
                    {b.status === 'streaming' && b.text && (
                      <span className="inline-block w-1.5 h-4 bg-tatweer-navy/70 ms-1 animate-pulse align-middle" />
                    )}
                  </div>
                )}
                {isError && onRetry && (
                  <div className="mt-3 pt-2 border-t border-rose-300/50 flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => onRetry(b.id)}
                      className="text-xs font-semibold rounded-full bg-rose-600 hover:bg-rose-700 text-white px-4 py-1.5 transition"
                    >
                      🔄 {retryLabel}
                    </button>
                    <span className="text-[10px] text-rose-600/70 uppercase tracking-wider">
                      {lang === 'ar' ? 'سنحاول بطريقة أخرى' : 'will use backup model'}
                    </span>
                  </div>
                )}
                {(showFooter || showReportOnRefused) && (
                  <div className="mt-2 pt-2 border-t border-tatweer-line flex items-center justify-between gap-2">
                    <span className="text-[10px] text-tatweer-muted uppercase tracking-wider">
                      {showFooter ? 'Powered by Tatweer AI' : ''}
                    </span>
                    {(showReport || showReportOnRefused) && (
                      submittedFor[b.id] ? (
                        <span className="text-[10px] text-tatweer-green/80">
                          {lang === 'ar' ? 'تم الإرسال — شكراً' : 'Submitted — thanks'}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setReportingFor(b.id)}
                          className="text-[10px] text-tatweer-muted/70 hover:text-tatweer-navy transition underline-offset-2 hover:underline"
                          aria-label={reportLabel}
                        >
                          {reportLabel}
                        </button>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {reportingBubble && (
        <ReportModal
          question={reportingBubble.question || ''}
          answer={reportingBubble.text}
          lang={reportingLang}
          onClose={() => setReportingFor(null)}
          onSuccess={() => handleSuccess(reportingBubble.id)}
        />
      )}
    </>
  );
}
